import { useAtomValue } from "jotai";
import React, { useEffect, useImperativeHandle } from "react";

import { Modal } from "./components/Modal";
import { useAddressBookActions } from "./hooks/useAddressBookActions";
import { isVisibleAtom } from "./stores";
import type { AddressBookMode, Category, OnChooserCallback } from "./types";

export interface AddressBookHandle {
  show: () => AddressBookHandle;
  hide: () => AddressBookHandle;
  setMode: (mode: AddressBookMode) => AddressBookHandle;
  setOnChooserCallback: (callback: OnChooserCallback) => AddressBookHandle;
  setSelectedContactWithId: (id: number) => AddressBookHandle;
  setSelectedCategoryWithIndex: (index: number) => AddressBookHandle;
  setSelectedCategory(type: Category["type"], key?: string): AddressBookHandle;
}

interface AddressBookProps {
  readonly isOpen?: boolean;
  /**
   * Renders the modal inside its closest positioned ancestor instead of over
   * the viewport, to embed the address book in a page region.
   */
  readonly contained?: boolean;
  readonly mode?: AddressBookMode;
  readonly onChooserCallback?: OnChooserCallback;
  readonly onClose?: () => void;
  readonly onCategoriesLoaded?: () => void;
  readonly ref?: React.Ref<AddressBookHandle>;
}

function AddressBookContent({
  isOpen,
  contained,
  mode,
  onChooserCallback,
  onClose,
  onCategoriesLoaded,
  ref,
}: AddressBookProps) {
  const isVisible = useAtomValue(isVisibleAtom);

  const {
    show,
    hide,
    loadCategories,
    setMode,
    setOnChooserCallback,
    setSelectedContactWithId,
    setSelectedCategoryWithIndex,
    setSelectedCategory,
  } = useAddressBookActions();

  // useEffect(() => {
  //   loadCategories();
  // }, [loadCategories]);

  // Load categories once on mount, then call onCategoriesLoaded when done
  useEffect(() => {
    loadCategories().then(() => {
      if (onCategoriesLoaded) {
        onCategoriesLoaded();
      }

      // setSelectedCategoryWithKey("YWxs").then(() => {
      //   console.debug("Categories loaded and category YWxs selected");
      //   setSelectedContactWithId(2658).then(() => {
      //     console.debug("Contact with ID 2658 selected");
      //     if (onCategoriesLoaded) {
      //       onCategoriesLoaded();
      //     }
      //   });
      // });
    });
  }, []);

  // Handle onClose callback
  useEffect(() => {
    if (!isVisible && onClose) {
      onClose();
    }
  }, [isVisible, onClose]);

  const handle: AddressBookHandle = {
    show: () => {
      show();
      return handle;
    },
    hide: () => {
      hide();
      return handle;
    },
    setMode: (mode) => {
      setMode(mode);
      return handle;
    },
    setOnChooserCallback: (callback) => {
      setOnChooserCallback(callback);
      return handle;
    },
    setSelectedContactWithId: (id) => {
      setSelectedContactWithId(id);
      return handle;
    },
    setSelectedCategoryWithIndex: (index) => {
      setSelectedCategoryWithIndex(index);
      return handle;
    },
    setSelectedCategory: (type, key) => {
      setSelectedCategory(type, key);
      return handle;
    },
  };

  useImperativeHandle(ref, () => handle, []);

  return <Modal contained={contained} />;
}

export function AddressBook({
  isOpen,
  contained,
  mode,
  onChooserCallback,
  onClose,
  onCategoriesLoaded,
  ref,
}: AddressBookProps) {
  return (
    <AddressBookContent
      isOpen={isOpen}
      contained={contained}
      mode={mode}
      onChooserCallback={onChooserCallback}
      onClose={onClose}
      onCategoriesLoaded={onCategoriesLoaded}
      ref={ref}
    />
  );
}
